import {
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    CircularProgress,
    Stack,
    Chip,
    alpha,
    Dialog,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getPhoto, getPhotosTimeline } from '../../../http/complexesApi';
import { unixTimeToString } from '../../../utils/converters';
import { useSelector } from 'react-redux';



// Иконки
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GridOnIcon from '@mui/icons-material/GridOn';

// Парсит WKT POLYGON((x1 y1,x2 y2,...)) в массив [x,y]
function parsePolygon(geom) {
    if (!geom || typeof geom !== 'string') return [];
    const m = geom.match(/POLYGON\s*\(\s*\(\s*([^)]+)\s*\)\s*\)/);
    if (!m) return [];
    return m[1].split(',')
        .map(s => s.trim().split(/\s+/).map(Number))
        .filter(p => p.length >= 2);
}

const zoneColors = {
    'regular': '#FFFFFF',
    "vehicleInteriorRecognize": '#FD5E21',
    "wrongWay": '#C57FFD',
    "maneuverCompletion": '#00FF00',
    "entranceToCrossroads": '#FFFF00',
    "beforeStopLine": '#F95B63',
    "afterStopLine": '#E29014'

}

// Центр полигона (среднее вершин) для размещения подписи
function getPolygonCenter(points) {
    if (!points?.length) return null;
    let sx = 0, sy = 0;
    for (const [x, y] of points) { sx += x; sy += y; }
    return { x: sx / points.length, y: sy / points.length };
}

const ComplexPhotos = ({ host, hostType = 'default' }) => {
    // Разные интервалы для разных типов хостов
    const FRAME_INTERVAL = useMemo(() => {
        if (hostType === 'crossroad') {
            return 5 * 60 * 1000; // 5 минут
        } else if (hostType === 'speed' || hostType === 'parking') {
            return 30 * 60 * 1000; // 30 минут
        } else if (hostType === 'vgk') {
            return 10 * 60 * 1000; // 5 минут
        }
        return 5 * 60 * 1000; // по умолчанию 5 минут
    }, [hostType]);

    const MIN_TIMELINE_HOURS = 24;

    const isDarkMode = useSelector(state => state.settings.darkMode);

    // Определяем мобильный режим
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [hosts, setHosts] = useState([]);
    const [hostid, setHostid] = useState(null);
    const [timeline, setTimeline] = useState([]);

    // Вычисляем период шкалы на основе самого раннего фото
    const timelineHours = useMemo(() => {
        if (!timeline.length) return MIN_TIMELINE_HOURS;

        const now = Date.now();
        const earliestTime = timeline.reduce((min, t) => {
            const time = t.length > 10 ? parseInt(t) : (parseInt(t) * 1000);
            return Math.min(min, time);
        }, now);

        const hoursFromEarliest = Math.ceil((now - earliestTime) / (60 * 60 * 1000));
        return Math.max(MIN_TIMELINE_HOURS, hoursFromEarliest + 1); // +1 час запас
    }, [timeline]);

    const TIMELINE_SLOTS = useMemo(() => {
        return (timelineHours * 60 * 60 * 1000) / FRAME_INTERVAL;
    }, [FRAME_INTERVAL, timelineHours]);
    const [image, setImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [timelineLoading, setTimelineLoading] = useState(true);
    const [fullscreen, setFullscreen] = useState(false);
    const [timelineScale, setTimelineScale] = useState(() => {
        // Восстанавливаем масштаб из localStorage
        const saved = localStorage.getItem('complexPhotos_timelineScale');
        return saved ? parseFloat(saved) : 1;
    });

    // Сохраняем масштаб в localStorage при изменении
    useEffect(() => {
        localStorage.setItem('complexPhotos_timelineScale', timelineScale.toString());
    }, [timelineScale]);

    const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
    const [isPanning, setIsPanning] = useState(false);
    const panStart = useRef({ x: 0, y: 0 });

    const containerRef = useRef(null);
    const activeSlotRef = useRef(null);
    const timelineScrollRef = useRef(null);

    // Разметка recognition (roi, lanes, zones) из item "recognition"
    const recognitionData = useMemo(() => {
        console.log(host, hostid)
        const currentHost = host.child ? host?.child?.find(child => child.host === hostid) : host
        console.log(currentHost)
        if (!currentHost?.items) return null;
        const item = currentHost.items.find(i => (i.name === 'recognition' || i.key_ === 'recognition'));
        const raw = item?.lastvalue ?? item?.value;
        if (raw == null) return null;
        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            const data = parsed?.body?.value ?? parsed;
            const roi = Array.isArray(data?.roi) ? data.roi : [];
            const lanes = Array.isArray(data?.lanes) ? data.lanes : [];
            const zones = Array.isArray(data?.zones) ? data.zones : [];
            if (roi.length === 0 && lanes.length === 0 && zones.length === 0) return null;
            return { roi, lanes, zones };
        } catch {
            return null;
        }
    }, [host, hostid]);

    const [imageNaturalSize, setImageNaturalSize] = useState(null);
    const [showRecognitionOverlay, setShowRecognitionOverlay] = useState(true);

    // Обработчик колёсика для шкалы: обычное - прокрутка, Ctrl - масштаб
    const handleTimelineWheel = useCallback((e) => {
        e.preventDefault();

        if (e.ctrlKey) {
            // Ctrl + колёсико = масштаб
            const delta = e.deltaY > 0 ? 0.9 : 1.1;
            setTimelineScale(prev => Math.min(5, Math.max(0.5, prev * delta)));
        } else {
            // Обычное колёсико = прокрутка
            e.currentTarget.scrollLeft += e.deltaY * 2;
        }
    }, []);

    const availableHosts = useMemo(() => {
        const result = [];

        if (hostType === 'crossroad' && host?.child) {
            host.child.forEach(child => {
                const axisTag = child.tags?.find(t => t.value === 'axis' || t.value === 'idis' || t.value === 'apix');
                if (axisTag) {
                    const script = child.scripts?.find(s => s.type === '6');
                    result.push({
                        hostid: child.host,
                        name: child.name.split('/')[1] || child.name,
                        url: script?.url || ''
                    });
                }

                const vscTag = child.tags?.find(t => t.value === 'vsc');
                if (vscTag) {
                    const ip = child.interfaces?.find(i => i.type === '1' && i.ip?.includes('192.168.'))?.ip;
                    if (ip) {
                        if (child.items?.find(i => i.name === '10.245.0.1' && i.status === '0')) {
                            result.push({
                                hostid: `${child.host}/245`,
                                name: `${child.name.split('/')[1] || child.name} :: 245`,
                                url: `http://${ip}:24582/video/data.mjpg`
                            });
                        }
                        if (child.items?.find(i => i.name === '10.246.0.1' && i.status === '0')) {
                            result.push({
                                hostid: `${child.host}/246`,
                                name: `${child.name.split('/')[1] || child.name} :: 246`,
                                url: `http://${ip}:24682/video/data.mjpg`
                            });
                        }
                    }
                }

                const factorTag = child.tags?.find(t => t.value === 'factor');
                if (factorTag) {
                    const ip = child.interfaces?.find(i => i.type === '1' && i.ip?.includes('192.168.'))?.ip;
                    result.push({
                        hostid: child.host,
                        name: child.name.split('/')[1] || child.name,
                        url: `http://${ip}:80/vision/last_photo`
                    });
                }
            });
        }

        if (hostType === 'vgk') {
            const childs = host.macros.filter(macro => macro.macro.startsWith('{$PHOTO_'))
            childs.map(child => {

                result.push({
                    hostid: `${host.host}/${child.macro.slice(0, -1).split('_URL_')?.[1]?.toLowerCase()}`,
                    name: child.description,
                    url: child.value
                })
            })
        }

        if (hostType === 'speed' || hostType === 'parking')
            result.push({
                hostid: host.host,
                name: host.name,
                url: null
            })


        console.log(result)
        return result.sort((a, b) => a.name.localeCompare(b.name));
    }, [hostType, host]);

    const timelineData = useMemo(() => {
        const now = new Date();
        const roundedNow = new Date(Math.ceil(now.getTime() / FRAME_INTERVAL) * FRAME_INTERVAL);
        const slots = [];

        // Создаем слоты от старых к новым (слева направо), чтобы "сейчас" было справа
        for (let i = TIMELINE_SLOTS - 1; i >= 0; i--) {
            const slotEnd = roundedNow.getTime() - i * FRAME_INTERVAL;
            const slotStart = slotEnd - FRAME_INTERVAL;

            const frameTime = timeline.find(t => {
                const time = t.length > 10 ? parseInt(t) : (parseInt(t) * 1000)
                return time >= slotStart && time < slotEnd
            });
            const isActive = image && (image.time * 1000) >= slotStart && (image.time * 1000) < slotEnd;

            slots.push({
                index: i,
                start: slotStart,
                end: slotEnd,
                time: new Date(slotEnd),
                frameTime,
                hasFrame: !!frameTime,
                isActive
            });
        }
        let inGap = false;
        let gapCount = 0;

        // Логика определения пропусков (идем слева направо)
        for (let i = 0; i < slots.length; i++) {
            if (slots[i].hasFrame) {
                if (inGap && gapCount > 0) {
                    slots[i - 1].gapEnd = true;
                    slots[i - 1].gapSize = gapCount;
                }
                inGap = true;
                gapCount = 0;
            } else if (inGap) {
                gapCount++;
                slots[i].isGap = true;
            }
        }

        return slots;
    }, [timeline, image, FRAME_INTERVAL, TIMELINE_SLOTS]);

    const stats = useMemo(() => {
        const total = timelineData.length;
        const available = timelineData.filter(s => s.hasFrame).length;
        const gaps = timelineData.filter(s => s.gapEnd).length;
        return { total, available, gaps, hours: timelineHours };
    }, [timelineData, timelineHours]);

    // Генерация меток времени для шкалы - каждые 3 часа + дневные метки
    const timelineLabels = useMemo(() => {
        const labels = [];
        const totalHours = timelineHours;

        // Метка "Сейчас" в конце
        labels.push({ position: 100, label: 'Сейчас', type: 'now' });

        // Генерируем метки каждые 3 часа
        for (let h = 3; h <= totalHours; h += 3) {
            const position = ((totalHours - h) / totalHours) * 100;

            // Проверяем, кратен ли час 24 (целый день)
            if (h % 24 === 0) {
                const days = h / 24;
                labels.push({
                    position,
                    label: `-${days}д`,
                    type: 'day'
                });
            } else {
                // Обычная часовая метка
                labels.push({
                    position,
                    label: `-${h}ч`,
                    type: 'hour'
                });
            }
        }

        // Сортируем по позиции (от начала к концу)
        return labels.sort((a, b) => a.position - b.position);
    }, [timelineHours]);

    useEffect(() => {
        setHosts(availableHosts);
        if (availableHosts.length > 0 && !hostid) {
            setHostid(availableHosts[0].hostid);
        }
    }, [availableHosts, hostid]);

    const loadImage = useCallback((time) => {
        if (!hostid || !time) return;

        setLoading(true);
        setImageNaturalSize(null);
        getPhoto(hostid, time)
            .then(res => {
                setImage(res);
                setTransform({ x: 0, y: 0, scale: 1 });
            })
            .catch(() => setImage(null))
            .finally(() => setLoading(false));
    }, [hostid]);

    useEffect(() => {
        if (!hostid) return;

        setImage(null);
        setTimeline([]);
        setTimelineLoading(true);

        getPhotosTimeline(hostid)
            .then(res => {
                const data = Array.isArray(res) ? res : [];
                setTimeline(data);
                if (data.length > 0) {
                    loadImage(data[data.length - 1]);
                }
            })
            .catch(() => setTimeline([]))
            .finally(() => setTimelineLoading(false));
    }, [hostid, loadImage]);

    useEffect(() => {
        if (!hostid) return;

        const interval = setInterval(() => {
            getPhotosTimeline(hostid)
                .then(res => setTimeline(Array.isArray(res) ? res : []))
                .catch(() => { });
        }, FRAME_INTERVAL);

        return () => clearInterval(interval);
    }, [hostid, FRAME_INTERVAL]);

    // Функция для центрирования активного слота в контейнере
    const scrollToActiveSlot = useCallback((smooth = true) => {
        if (!activeSlotRef.current || !timelineScrollRef.current) return;

        const container = timelineScrollRef.current;
        const slot = activeSlotRef.current;

        // Позиция слота относительно контейнера
        const slotRect = slot.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Вычисляем целевую позицию (центрируем слот)
        const slotCenter = slot.offsetLeft + slot.offsetWidth / 2;
        const containerCenter = container.clientWidth / 2;
        const targetScroll = slotCenter - containerCenter;

        if (smooth) {
            container.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
            });
        } else {
            container.scrollLeft = targetScroll;
        }
    }, []);

    // Прокрутка к активному слоту на шкале при смене изображения
    useEffect(() => {
        if (activeSlotRef.current && timelineScrollRef.current) {
            setTimeout(() => scrollToActiveSlot(true), 50);
        }
    }, [image, scrollToActiveSlot]);

    // Прокрутка к активному слоту при изменении масштаба
    useEffect(() => {
        if (activeSlotRef.current && timelineScrollRef.current) {
            // Задержка для обновления DOM после изменения ширины
            setTimeout(() => scrollToActiveSlot(true), 150);
        }
    }, [timelineScale, scrollToActiveSlot]);

    // Прокрутка шкалы к концу при первой загрузке timeline
    useEffect(() => {
        if (timelineScrollRef.current && timeline.length > 0) {
            setTimeout(() => {
                if (timelineScrollRef.current) {
                    timelineScrollRef.current.scrollLeft = timelineScrollRef.current.scrollWidth;
                }
            }, 100);
        }
    }, [hostid]);

    const navigate = useCallback((direction) => {
        if (!image || !timeline.length) return;

        const currentIndex = timeline.indexOf(image.time);
        let newIndex;

        switch (direction) {
            case 'first': newIndex = 0; break;
            case 'prev': newIndex = Math.max(0, currentIndex - 1); break;
            case 'next': newIndex = Math.min(timeline.length - 1, currentIndex + 1); break;
            case 'last': newIndex = timeline.length - 1; break;
            default: return;
        }

        if (newIndex !== currentIndex) {
            loadImage(timeline[newIndex]);
        }
    }, [image, timeline, loadImage]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            switch (e.code) {
                case 'ArrowLeft': navigate('prev'); break;
                case 'ArrowRight': navigate('next'); break;
                case 'Home': navigate('first'); break;
                case 'End': navigate('last'); break;
                case 'Escape': setFullscreen(false); break;
                default: break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [navigate]);

    const handleWheel = useCallback((e) => {
        if (!image) return;
        e.preventDefault();

        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setTransform(prev => ({
            ...prev,
            scale: Math.min(5, Math.max(0.5, prev.scale * delta))
        }));
    }, [image]);

    const handleMouseDown = useCallback((e) => {
        if (!image || transform.scale <= 1) return;
        e.preventDefault();
        setIsPanning(true);
        panStart.current = { x: e.clientX - transform.x, y: e.clientY - transform.y };
    }, [image, transform]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isPanning) return;
            setTransform(prev => ({
                ...prev,
                x: e.clientX - panStart.current.x,
                y: e.clientY - panStart.current.y
            }));
        };

        const handleMouseUp = () => setIsPanning(false);

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isPanning]);

    const resetTransform = useCallback(() => {
        setTransform({ x: 0, y: 0, scale: 1 });
    }, []);

    // if (hostType !== 'crossroad' && hostType !== 'vgk') {
    //     return (
    //         <Box sx={{ 
    //             p: 4,
    //             textAlign: 'center',
    //             color: 'var(--text-muted)',
    //             backgroundColor: 'var(--background-default)',
    //             borderRadius: '12px',
    //             border: '1px solid var(--border-default)'
    //         }}>
    //             <VideocamOffIcon sx={{ fontSize: 56, mb: 2, opacity: 0.5 }} />
    //             <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: 'var(--text-primary)', mb: 0.5 }}>
    //                 Просмотр изображений недоступен
    //             </Typography>
    //             <Typography sx={{ fontSize: '0.85rem' }}>
    //                 Функция доступна только для перекрёстков и ВГК
    //             </Typography>
    //         </Box>
    //     );
    // }

    const renderImageViewer = (isFullscreen = false) => (
        <Box
            ref={!isFullscreen ? containerRef : null}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isFullscreen ? '#0d1117' : 'var(--background-default)',
                borderRadius: isFullscreen ? 0 : '8px',
                overflow: 'hidden',
                cursor: isPanning ? 'grabbing' : (image && transform.scale > 1 ? 'grab' : 'default'),
                userSelect: 'none',
                position: 'relative',
                minHeight: isFullscreen ? 0 : { xs: 250, md: 350 },
                maxHeight: isFullscreen ? '100%' : '100%'
            }}
        >
            {loading ? (
                <Box sx={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                    <CircularProgress size={{ xs: 28, md: 36 }} sx={{ color: 'var(--primary-color)' }} />
                    <Typography sx={{ mt: { xs: 1, md: 1.5 }, fontSize: { xs: '0.75rem', md: '0.8rem' } }}>Загрузка...</Typography>
                </Box>
            ) : image ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                        transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                        transition: isPanning ? 'none' : 'transform 0.1s ease-out'
                    }}
                >
                    <Box sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '98%',
                        height: '98%'
                    }}>
                        <img
                            src={image.img}
                            alt="Кадр"
                            draggable={false}
                            onDoubleClick={resetTransform}
                            onLoad={(e) => setImageNaturalSize({ width: e.target.naturalWidth, height: e.target.naturalHeight })}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                borderRadius: isFullscreen ? 0 : 8,
                                boxShadow: isFullscreen ? 'none' : '0 4px 24px rgba(0,0,0,0.2)'
                            }}
                        />
                        {recognitionData && imageNaturalSize && showRecognitionOverlay && (() => {
                            const textSize = Math.max(16, Math.min(40, imageNaturalSize.width / 80));
                            console.log(imageNaturalSize, textSize)
                            return (
                                <svg
                                    viewBox={`0 0 ${imageNaturalSize.width} ${imageNaturalSize.height}`}
                                    preserveAspectRatio="xMidYMid meet"
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    {/* ROI — белый контур */}
                                    {recognitionData.roi?.length >= 3 && (
                                        <polygon
                                            points={recognitionData.roi.map(([x, y]) => `${x},${y}`).join(' ')}
                                            fill="none"
                                            stroke="white"
                                            strokeWidth={8}
                                        />
                                    )}
                                    {/* Lanes — синий */}
                                    {recognitionData.lanes.map((l, i) => {
                                        const points = parsePolygon(l.geometry);
                                        if (points.length < 3) return null;
                                        const pointsStr = points.map(([x, y]) => `${x},${y}`).join(' ');
                                        const center = getPolygonCenter(points);
                                        const dirText = l.direction === 'in' ? 'на камеру' : l.direction === 'out' ? 'от камеры' : 'любое';
                                        const line1 = l.name || '—';
                                        const line2 = `Направление: ${dirText}`;
                                        const line3 = l.speedLimit != null && l.speedLimit !== '' ? `Ограничение: ${l.speedLimit} км/ч` : 'Ограничение: —';
                                        const lineHeight = 1.2 * textSize;
                                        return (
                                            <g key={l.id || `l-${i}`}>
                                                <polygon points={pointsStr} fill="none" stroke="#2980B9" strokeWidth={10} />
                                                {center && (
                                                    <text x={center.x} y={center.y - lineHeight} textAnchor="middle" fontSize={textSize} fontWeight={600} fill="#2980B9" stroke="rgb(0, 0, 0)" strokeWidth={4} paintOrder="stroke">
                                                        <tspan x={center.x} dy="0">{line1}</tspan>
                                                        <tspan x={center.x} dy={lineHeight}>{line2}</tspan>
                                                        <tspan x={center.x} dy={lineHeight}>{line3}</tspan>
                                                    </text>
                                                )}
                                            </g>
                                        );
                                    })}




                                    {/* Zones: vehicleInteriorRecognize — оранжевый, wrongWay — фиолетовый */}
                                    {recognitionData.zones.map((z, i) => {
                                        const points = parsePolygon(z.geometry);
                                        if (points.length < 3) return null;
                                        const pointsStr = points.map(([x, y]) => `${x},${y}`).join(' ');
                                        const stroke = zoneColors[z.type] || '#FFFFFF';
                                        const center = getPolygonCenter(points);
                                        const name = z.name || '';
                                        return (
                                            <g key={z.id || `z-${i}`}>
                                                <polygon points={pointsStr} fill="none" stroke={stroke} strokeWidth={8} strokeDasharray={z.type === 'wrongWay' ? 20 : null} />
                                                {center && name && (
                                                    <text x={center.x} y={center.y} textAnchor="middle" dominantBaseline="central" fontSize={textSize} fontWeight={600} fill={stroke} stroke="rgba(0,0,0,0.8)" strokeWidth={4} paintOrder="stroke">{name}</text>
                                                )}
                                            </g>
                                        );
                                    })}
                                </svg>
                            );
                        })()}
                    </Box>
                </Box>
            ) : (
                <Box sx={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                    <ImageNotSupportedIcon sx={{ fontSize: { xs: 36, md: 48 }, mb: { xs: 1, md: 1.5 }, opacity: 0.5 }} />
                    <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                        {timelineLoading ? 'Загрузка данных...' : 'Нет изображений'}
                    </Typography>
                </Box>
            )}
        </Box>
    );

    const renderTimeline = (isFullscreen = false) => (
        <Box sx={{
            p: { xs: 1, md: 1.5 },
            backgroundColor: isFullscreen ? 'rgba(13, 17, 23, 0.95)' : 'var(--background-subtle)',
            borderTop: isFullscreen ? 'none' : '1px solid var(--border-default)'
        }}>
            {/* Статистика */}
            <Stack
                direction="row"
                spacing={{ xs: 1, md: 1.5 }}
                alignItems="center"
                justifyContent="center"
                sx={{ mb: { xs: 0.75, md: 1 } }}
            >
                <Chip
                    icon={<PhotoCameraIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
                    label={`${stats.available} кадров`}
                    size="small"
                    sx={{
                        height: { xs: 20, md: 22 },
                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                        backgroundColor: alpha(isDarkMode ? '#3fb950' : '#1a7f37', 0.15),
                        color: 'var(--success-color)',
                        '& .MuiChip-icon': { color: 'inherit' },
                        '& .MuiChip-label': { px: { xs: 0.5, md: 0.75 } }
                    }}
                />
                {stats.gaps > 0 && (
                    <Chip
                        icon={<WarningAmberIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
                        label={`${stats.gaps} пропусков`}
                        size="small"
                        sx={{
                            height: { xs: 20, md: 22 },
                            fontSize: { xs: '0.65rem', md: '0.7rem' },
                            backgroundColor: alpha(isDarkMode ? '#d29922' : '#9a6700', 0.15),
                            color: 'var(--warning-color)',
                            '& .MuiChip-icon': { color: 'inherit' },
                            '& .MuiChip-label': { px: { xs: 0.5, md: 0.75 } }
                        }}
                    />
                )}
                <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
                    label={stats.hours >= 24 ? `${Math.floor(stats.hours / 24)}д ${stats.hours % 24}ч` : `${stats.hours}ч`}
                    size="small"
                    sx={{
                        height: { xs: 20, md: 22 },
                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                        backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1),
                        color: 'var(--primary-color)',
                        '& .MuiChip-icon': { color: 'inherit' },
                        '& .MuiChip-label': { px: { xs: 0.5, md: 0.75 } }
                    }}
                />

                {/* Кнопки масштаба шкалы */}
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Tooltip title="Уменьшить масштаб">
                        <IconButton
                            size="small"
                            onClick={() => setTimelineScale(prev => Math.max(0.5, prev * 0.8))}
                            disabled={timelineScale <= 0.5}
                            sx={{
                                width: 20,
                                height: 20,
                                border: `1px solid ${isDarkMode ? 'var(--border-default)' : 'var(--border-default)'}`,
                                borderRadius: '4px',
                                color: 'var(--text-secondary)',
                                backgroundColor: 'transparent',
                                '&:hover': {
                                    backgroundColor: 'var(--hover-bg)',
                                    borderColor: 'var(--primary-color)'
                                },
                                '&:disabled': { opacity: 0.3 }
                            }}
                        >
                            <ZoomOutIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={timelineScale !== 1 ? "Сбросить масштаб" : "Масштаб шкалы"}>
                        <Chip
                            label={`${Math.round(timelineScale * 100)}%`}
                            size="small"
                            onClick={() => timelineScale !== 1 && setTimelineScale(1)}
                            sx={{
                                height: 20,
                                minWidth: 45,
                                fontSize: '0.65rem',
                                fontWeight: 500,
                                backgroundColor: timelineScale !== 1
                                    ? alpha(isDarkMode ? '#a371f7' : '#8250df', 0.15)
                                    : alpha(isDarkMode ? '#8b949e' : '#57606a', 0.1),
                                color: timelineScale !== 1
                                    ? (isDarkMode ? '#a371f7' : '#8250df')
                                    : 'var(--text-muted)',
                                cursor: timelineScale !== 1 ? 'pointer' : 'default',
                                '& .MuiChip-label': { px: 0.75 },
                                '&:hover': timelineScale !== 1 ? {
                                    backgroundColor: alpha(isDarkMode ? '#a371f7' : '#8250df', 0.25)
                                } : {}
                            }}
                        />
                    </Tooltip>

                    <Tooltip title="Увеличить масштаб">
                        <IconButton
                            size="small"
                            onClick={() => setTimelineScale(prev => Math.min(5, prev * 1.25))}
                            disabled={timelineScale >= 5}
                            sx={{
                                width: 20,
                                height: 20,
                                border: `1px solid ${isDarkMode ? 'var(--border-default)' : 'var(--border-default)'}`,
                                borderRadius: '4px',
                                color: 'var(--text-secondary)',
                                backgroundColor: 'transparent',
                                '&:hover': {
                                    backgroundColor: 'var(--hover-bg)',
                                    borderColor: 'var(--primary-color)'
                                },
                                '&:disabled': { opacity: 0.3 }
                            }}
                        >
                            <ZoomInIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {/* Шкала с прокруткой */}
            <Box
                ref={!isFullscreen ? timelineScrollRef : null}
                onWheel={handleTimelineWheel}
                sx={{
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    borderRadius: { xs: '4px', md: '6px' },
                    border: `1px solid ${isFullscreen ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`,
                    backgroundColor: isFullscreen ? 'rgba(255,255,255,0.05)' : 'var(--background-default)',
                    '&::-webkit-scrollbar': {
                        height: 6
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: isFullscreen ? 'rgba(255,255,255,0.05)' : 'var(--background-subtle)'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: isFullscreen ? 'rgba(255,255,255,0.2)' : 'var(--border-default)',
                        borderRadius: 3,
                        '&:hover': {
                            backgroundColor: isFullscreen ? 'rgba(255,255,255,0.3)' : 'var(--text-muted)'
                        }
                    }
                }}>
                {/* Внутренний контейнер с шкалой и метками */}
                <Box sx={{
                    position: 'relative',
                    minWidth: `${Math.max(100, timelineHours * 4 * timelineScale * (isMobile ? 2 : 1))}%`,
                    width: timelineScale <= 1 && timelineHours <= 24 && !isMobile ? '100%' : 'auto',
                    transition: 'min-width 0.15s ease-out'
                }}>
                    {/* Шкала */}
                    <Box sx={{
                        display: 'flex',
                        gap: '1px',
                        height: { xs: 24, md: 28 }
                    }}>
                        {timelineData.map((slot, idx) => (
                            <Tooltip
                                key={idx}
                                title={
                                    <Box sx={{ textAlign: 'center', p: { xs: 0.4, md: 0.5 } }}>
                                        <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' }, fontWeight: 500 }}>
                                            {slot.time.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                                        </Typography>
                                        <Typography sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' }, opacity: 0.7 }}>
                                            {slot.time.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}
                                        </Typography>
                                        {slot.hasFrame && (
                                            <Typography sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' }, color: '#4caf50', mt: 0.5 }}>
                                                ● Есть кадр
                                            </Typography>
                                        )}
                                        {slot.gapEnd && (
                                            <Typography sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' }, color: '#ff9800', mt: 0.5 }}>
                                                ⚠ Пропуск: {slot.gapSize}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                arrow
                                placement="top"
                            >
                                <Box
                                    ref={slot.isActive ? activeSlotRef : null}
                                    onClick={() => slot.frameTime && loadImage(slot.frameTime)}
                                    sx={{
                                        flex: 1,
                                        minWidth: 2,
                                        cursor: slot.hasFrame ? 'pointer' : 'default',
                                        backgroundColor: slot.isActive
                                            ? 'var(--primary-color)'
                                            : slot.hasFrame
                                                ? 'var(--success-color)'
                                                : slot.isGap
                                                    ? alpha(isDarkMode ? '#d29922' : '#9a6700', 0.25)
                                                    : 'transparent',
                                        borderBottom: slot.gapEnd ? '2px solid var(--warning-color)' : 'none',
                                        borderTop: slot.isActive ? '2px solid var(--primary-color)' : 'none',
                                        borderLeft: slot.isActive ? '2px solid var(--primary-color)' : 'none',
                                        borderRight: slot.isActive ? '2px solid var(--primary-color)' : 'none',
                                        boxShadow: slot.isActive ? '0 0 8px rgba(88, 166, 255, 0.6)' : 'none',
                                        transform: slot.isActive ? 'scaleY(1.3)' : 'scaleY(1)',
                                        zIndex: slot.isActive ? 10 : 1,
                                        transition: 'all 0.15s ease',
                                        '&:hover': slot.hasFrame ? {
                                            backgroundColor: slot.isActive
                                                ? 'var(--primary-dark)'
                                                : alpha(isDarkMode ? '#3fb950' : '#1a7f37', 0.7),
                                            transform: slot.isActive ? 'scaleY(1.3)' : 'scaleY(1.15)'
                                        } : {}
                                    }}
                                />
                            </Tooltip>
                        ))}
                    </Box>

                    {/* Метки времени - внутри прокручиваемого контейнера */}
                    <Box sx={{
                        position: 'relative',
                        mt: { xs: 0.5, md: 0.75 },
                        mb: { xs: 0.5, md: 0.5 },
                        fontFamily: 'monospace',
                        height: 14
                    }}>
                        {timelineLabels.map((label, idx) => (
                            <span key={idx} style={{
                                position: 'absolute',
                                left: `${label.position}%`,
                                transform: 'translateX(-50%)',
                                whiteSpace: 'nowrap',
                                fontSize: label.type === 'day' ? '0.65rem' : label.type === 'now' ? '0.65rem' : '0.6rem',
                                fontWeight: label.type === 'day' ? 600 : 400,
                                color: isFullscreen
                                    ? (label.type === 'day' ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.35)')
                                    : (label.type === 'day' ? 'var(--text-secondary)' : 'var(--text-muted)')
                            }}>
                                {label.label}
                            </span>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );

    const renderControls = (isFullscreen = false) => {
        const buttonStyle = {
            width: { xs: 28, md: 32 },
            height: { xs: 28, md: 32 },
            border: `1px solid ${isDarkMode ? 'var(--border-default)' : 'var(--border-default)'}`,
            borderRadius: { xs: '4px', md: '6px' },
            color: isDarkMode ? 'var(--text-secondary)' : 'var(--text-secondary)',
            backgroundColor: isDarkMode ? 'var(--background-paper)' : 'transparent',
            '&:hover': {
                backgroundColor: isDarkMode ? 'var(--hover-bg)' : 'var(--hover-bg)',
                borderColor: 'var(--primary-color)'
            },
            '&:disabled': {
                opacity: 0.3,
                color: isDarkMode ? 'var(--text-muted)' : 'var(--text-muted)'
            }
        };

        const buttonStyleFullscreen = {
            width: { xs: 28, md: 32 },
            height: { xs: 28, md: 32 },
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: { xs: '4px', md: '6px' },
            color: 'rgba(255,255,255,0.9)',
            backgroundColor: 'rgba(255,255,255,0.05)',
            '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderColor: 'rgba(255,255,255,0.4)'
            },
            '&:disabled': { opacity: 0.3 }
        };

        const currentButtonStyle = isFullscreen ? buttonStyleFullscreen : buttonStyle;

        return (
            <Box sx={{
                p: { xs: 1, md: 1.5 },
                backgroundColor: isFullscreen ? 'rgba(13, 17, 23, 0.95)' : 'var(--background-subtle)',
                borderTop: `1px solid ${isFullscreen ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`,
                gap: 4,
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap'
            }}>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={{ xs: 0.75, md: 1 }} flexWrap="wrap">
                    {/* Время */}
                    {image && (
                        <Stack direction="row" alignItems="center" spacing={{ xs: 0.4, md: 0.5 }} sx={{ mr: { xs: 0.75, md: 1 } }}>
                            <ScheduleIcon sx={{ fontSize: { xs: 14, md: 16 }, color: isFullscreen ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)' }} />
                            <Typography sx={{
                                fontSize: { xs: '0.75rem', md: '0.8rem' },
                                color: isFullscreen ? 'rgba(255,255,255,0.9)' : 'var(--text-primary)',
                                fontWeight: 500
                            }}>
                                {unixTimeToString(image.time)}
                            </Typography>
                        </Stack>
                    )}
                    <Box sx={{
                        p: { xs: 1, md: 1.5 },
                        gap: 1,
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row'
                    }}>
                        {/* Кнопки навигации */}
                        <Tooltip title="Первый">
                            <IconButton
                                size="small"
                                onClick={() => navigate('first')}
                                disabled={!image || timeline.indexOf(image?.time) === 0}
                                sx={currentButtonStyle}
                            >
                                <FirstPageIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Предыдущий">
                            <IconButton
                                size="small"
                                onClick={() => navigate('prev')}
                                disabled={!image || timeline.indexOf(image?.time) === 0}
                                sx={currentButtonStyle}
                            >
                                <ChevronLeftIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                            </IconButton>
                        </Tooltip>

                        {/* Счётчик */}
                        {image && (
                            <Chip
                                label={`${timeline.indexOf(image.time) + 1} / ${timeline.length}`}
                                size="small"
                                sx={{
                                    height: { xs: 20, md: 24 },
                                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                                    fontWeight: 600,
                                    backgroundColor: isFullscreen
                                        ? 'rgba(88, 166, 255, 0.2)'
                                        : alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.15),
                                    color: isFullscreen ? 'rgba(255,255,255,0.9)' : 'var(--primary-color)',
                                    border: `1px solid ${isFullscreen
                                        ? 'rgba(88, 166, 255, 0.4)'
                                        : alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.3)}`
                                }}
                            />
                        )}

                        <Tooltip title="Следующий">
                            <IconButton
                                size="small"
                                onClick={() => navigate('next')}
                                disabled={!image || timeline.indexOf(image?.time) === timeline.length - 1}
                                sx={currentButtonStyle}
                            >
                                <ChevronRightIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Последний">
                            <IconButton
                                size="small"
                                onClick={() => navigate('last')}
                                disabled={!image || timeline.indexOf(image?.time) === timeline.length - 1}
                                sx={currentButtonStyle}
                            >
                                <LastPageIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                            </IconButton>
                        </Tooltip>

                        <Box sx={{ width: { xs: 8, md: 12 } }} />

                        <Tooltip title="Приблизить">
                            <IconButton
                                size="small"
                                onClick={() => setTransform(prev => ({
                                    ...prev,
                                    scale: Math.min(5, prev.scale * 1.2)
                                }))}
                                disabled={!image}
                                sx={currentButtonStyle}
                            >
                                <ZoomInIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Отдалить">
                            <IconButton
                                size="small"
                                onClick={() => setTransform(prev => ({
                                    ...prev,
                                    scale: Math.max(0.5, prev.scale * 0.8)
                                }))}
                                disabled={!image}
                                sx={currentButtonStyle}
                            >
                                <ZoomOutIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                            </IconButton>
                        </Tooltip>

                        {transform.scale !== 1 && (
                            <Tooltip title="Сбросить масштаб">
                                <IconButton
                                    size="small"
                                    onClick={resetTransform}
                                    sx={{
                                        ...currentButtonStyle,
                                        color: 'var(--primary-color)',
                                        borderColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.5),
                                        '&:hover': {
                                            backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.15),
                                            borderColor: 'var(--primary-color)'
                                        }
                                    }}
                                >
                                    <ZoomOutMapIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                                </IconButton>
                            </Tooltip>
                        )}

                        {recognitionData && (
                            <Tooltip title={showRecognitionOverlay ? 'Скрыть разметку (lanes/zones)' : 'Показать разметку'}>
                                <IconButton
                                    size="small"
                                    onClick={() => setShowRecognitionOverlay(v => !v)}
                                    sx={{
                                        ...currentButtonStyle,
                                        color: showRecognitionOverlay ? 'var(--primary-color)' : undefined,
                                        borderColor: showRecognitionOverlay ? alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.5) : undefined
                                    }}
                                >
                                    <GridOnIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                                </IconButton>
                            </Tooltip>
                        )}

                        {!isFullscreen ? (
                            <Tooltip title="Полный экран">
                                <IconButton
                                    size="small"
                                    onClick={() => setFullscreen(true)}
                                    disabled={!image}
                                    sx={currentButtonStyle}
                                >
                                    <FullscreenIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip title="Закрыть">
                                <IconButton
                                    size="small"
                                    onClick={() => setFullscreen(false)}
                                    sx={{
                                        ...buttonStyleFullscreen,
                                        borderColor: 'rgba(248, 81, 73, 0.5)',
                                        color: '#f85149',
                                        '&:hover': {
                                            backgroundColor: 'rgba(248, 81, 73, 0.2)',
                                            borderColor: '#f85149'
                                        }
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Stack>

            </Box>
        );
    };

    const handleFullscreenClose = () => {
        setFullscreen(false);
        resetTransform();
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            // height: { xs: '60vh', md: '65vh' },
            backgroundColor: 'var(--background-paper)',
            // borderRadius: { xs: '8px', md: '12px' },
            overflow: 'hidden',
            border: '1px solid var(--border-default)'
        }}>
            {/* Заголовок с выбором камеры */}
            {availableHosts.length > 1 && (
                <Box sx={{
                    p: { xs: 1, md: 1.5 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, md: 1.5 },
                    borderBottom: '1px solid var(--border-default)',
                    backgroundColor: 'var(--background-subtle)'
                }}>
                    <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, md: 0.75 }}>
                        <CameraAltIcon sx={{ fontSize: { xs: 16, md: 18 }, color: 'var(--primary-color)' }} />
                        <Typography sx={{
                            fontSize: { xs: '0.75rem', md: '0.8rem' },
                            fontWeight: 500,
                            color: 'var(--text-secondary)',
                            whiteSpace: 'nowrap'
                        }}>
                            Камера
                        </Typography>
                    </Stack>

                    <FormControl size="small" sx={{ minWidth: { xs: 120, md: 180 }, flex: 1, maxWidth: { xs: 200, md: 320 } }}>
                        <Select
                            value={hostid || ''}
                            onChange={(e) => setHostid(e.target.value)}
                            displayEmpty
                            sx={{
                                backgroundColor: isDarkMode ? 'var(--background-paper)' : 'var(--background-paper)',
                                color: 'var(--text-primary)',
                                fontSize: { xs: '0.7rem', md: '0.75rem' },
                                height: { xs: 28, md: 32 },
                                borderRadius: { xs: '6px', md: '8px' },
                                '& .MuiSelect-select': { py: { xs: 0.4, md: 0.5 } },
                                '& .MuiSelect-icon': { color: 'var(--text-secondary)' },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isDarkMode ? 'var(--border-default)' : 'var(--border-default)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--primary-color)'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--primary-color)'
                                }
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: isDarkMode ? '#161b22' : '#ffffff',
                                        backgroundImage: 'none',
                                        border: `1px solid ${isDarkMode ? '#30363d' : '#d0d7de'}`,
                                        borderRadius: { xs: '6px', md: '8px' },
                                        boxShadow: isDarkMode
                                            ? '0 8px 24px rgba(0, 0, 0, 0.4)'
                                            : '0 8px 24px rgba(0, 0, 0, 0.15)',
                                        mt: 0.5,
                                        '& .MuiMenuItem-root': {
                                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                                            minHeight: { xs: 28, md: 32 },
                                            color: isDarkMode ? '#f0f6fc' : '#24292f',
                                            '&:hover': {
                                                backgroundColor: isDarkMode
                                                    ? 'rgba(177, 186, 196, 0.12)'
                                                    : 'rgba(208, 215, 222, 0.32)'
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: isDarkMode
                                                    ? 'rgba(88, 166, 255, 0.15)'
                                                    : 'rgba(9, 105, 218, 0.08)',
                                                color: isDarkMode ? '#58a6ff' : '#0969da',
                                                '&:hover': {
                                                    backgroundColor: isDarkMode
                                                        ? 'rgba(88, 166, 255, 0.2)'
                                                        : 'rgba(9, 105, 218, 0.12)'
                                                }
                                            }
                                        }
                                    }
                                }
                            }}
                        >
                            {hosts.map(h => (
                                <MenuItem key={h.hostid} value={h.hostid}>{h.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {hosts.find(h => h.hostid === hostid)?.url && (
                        <Typography
                            component="a"
                            href={hosts.find(h => h.hostid === hostid).url}
                            target="_blank"
                            sx={{
                                fontSize: { xs: '0.65rem', md: '0.7rem' },
                                color: 'var(--primary-color)',
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': { textDecoration: 'underline' }
                            }}
                        >
                            Поток ↗
                        </Typography>
                    )}
                </Box>
            )}
            {/* Область просмотра */}
            <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {renderImageViewer()}
            </Box>

            {/* Управление */}
            <Box sx={{ flexShrink: 0 }}>
                {renderControls()}
            </Box>

            {/* Временная шкала */}
            <Box sx={{ flexShrink: 0 }}>
                {renderTimeline()}
            </Box>

            {/* Полноэкранный режим */}
            <Dialog
                open={fullscreen}
                onClose={handleFullscreenClose}
                fullScreen
                PaperProps={{
                    sx: {
                        backgroundColor: '#0d1117',
                        m: 0,
                        maxHeight: '100vh',
                        height: '100vh',
                        borderRadius: 0,
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    overflow: 'hidden'
                }}>
                    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        {renderImageViewer(true)}
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                        {renderControls(true)}
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                        {renderTimeline(true)}
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default ComplexPhotos;
